import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  RefreshCw, 
  Eye, 
  Building2,
  Users,
  Plus,
  MapPin,
  Briefcase,
  ArrowUpDown
} from "lucide-react";
import AddCompanyDialog from "@/components/AddCompanyDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompaniesPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("alpha_asc");

  useEffect(() => {
    fetchCompanies();
  }, [search, sortBy]);

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (sortBy) params.append('sort', sortBy);
      
      const response = await axios.get(`${API}/companies?${params.toString()}`, {
        withCredentials: true
      });
      setCompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to fetch companies");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Companies</h2>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage your company database</p>
        </div>
        <div className="flex items-center space-x-2">
          <AddCompanyDialog onCompanyAdded={fetchCompanies} />
          <Button 
            onClick={fetchCompanies}
            variant="outline"
            data-testid="refresh-companies-btn"
            className="px-3"
          >
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search companies by name, industry, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 bg-white/80 backdrop-blur-sm"
            data-testid="search-companies-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-5 h-5 text-slate-400" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[200px] h-12 bg-white/80 backdrop-blur-sm">
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alpha_asc">A → Z (Alphabetical)</SelectItem>
              <SelectItem value="alpha_desc">Z → A (Reverse)</SelectItem>
              <SelectItem value="created_newest">Newest First</SelectItem>
              <SelectItem value="created_oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Total Companies</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{companies.length}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      {companies.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            {search ? "No companies match your search" : "No companies found"}
          </h3>
          <p className="text-slate-600 mb-6">
            {search 
              ? `Try adjusting your search terms or clear the search to see all companies` 
              : `Add your first company to get started`}
          </p>
          {search ? (
            <Button onClick={() => setSearch("")} variant="outline">
              Clear Search
            </Button>
          ) : (
            <AddCompanyDialog onCompanyAdded={fetchCompanies} />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="companies-grid">
          {companies.map((company) => (
            <Card 
              key={company.id} 
              className="glass-card p-6 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate(`/companies/${company.id}`)}
              data-testid={`company-card-${company.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center text-white text-xl font-bold">
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/companies/${company.id}`);
                  }}
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
              
              <h3 className="text-lg font-bold text-slate-900 mb-2">{company.name}</h3>
              
              <div className="space-y-2 text-sm text-slate-600">
                {company.industry && (
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span>{company.industry}</span>
                  </div>
                )}
                {company.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{company.location}</span>
                  </div>
                )}
                {company.size && (
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span>{company.size} employees</span>
                  </div>
                )}
              </div>

              {company.revenue && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {company.revenue}
                  </Badge>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
